/**
 * Budget Alerts Page
 * 
 * Main page for managing budget alerts.
 * Features:
 * - View and manage budget alert configurations
 * - Create new alert configurations
 * - View alert history
 * - Check budget status with spending
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import {
  budgetAlertService,
  type BudgetAlertConfig,
  type BudgetAlertHistory,
  type BudgetStatus,
} from '../api';
import {
  Button,
  Card,
  Badge,
  PageHeader,
  PageLoading,
  NoOrganizationMessage,
  Modal,
  Input,
  Select,
  Table,
  formatEuro,
  formatDateTime,
} from '../components';

// Alert type display mapping
const alertTypeLabels: Record<string, string> = {
  approaching: 'Lähestymässä rajaa',
  exceeded: 'Ylitetty',
};

const alertTypeColors: Record<string, string> = {
  approaching: 'bg-yellow-100 text-yellow-800',
  exceeded: 'bg-red-100 text-red-800',
};

// Threshold level display
function getThresholdLevel(percent: number): { label: string; color: string } {
  if (percent >= 100) return { label: 'Kriittinen (100%)', color: 'bg-red-100 text-red-800' };
  if (percent >= 90) return { label: 'Varoitus (90%)', color: 'bg-orange-100 text-orange-800' };
  if (percent >= 75) return { label: 'Info (75%)', color: 'bg-blue-100 text-blue-800' };
  return { label: `${percent}%`, color: 'bg-gray-100 text-gray-800' };
}

export default function BudgetAlertsPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [configs, setConfigs] = useState<BudgetAlertConfig[]>([]);
  const [history, setHistory] = useState<BudgetAlertHistory[]>([]);
  const [budgetStatus, setBudgetStatus] = useState<BudgetStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'configs' | 'history' | 'status'>('configs');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<BudgetAlertConfig | null>(null);

  useEffect(() => {
    if (activeOrg) {
      loadData();
    }
  }, [activeOrg, activeTab]);

  const loadData = async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'configs') {
        const configsData = await budgetAlertService.listConfigs(activeOrg.id);
        setConfigs(configsData);
      } else if (activeTab === 'history') {
        const historyData = await budgetAlertService.getHistory(activeOrg.id, { limit: 50 });
        setHistory(historyData.history);
      } else if (activeTab === 'status') {
        const statusData = await budgetAlertService.getBudgetStatus(activeOrg.id);
        setBudgetStatus(statusData.budgets);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async (config: {
    budget_id: string;
    line_item_id?: string;
    alert_type: 'approaching' | 'exceeded';
    threshold_percent: number;
    email_enabled: boolean;
    notification_enabled: boolean;
    recipients: string[];
    cooldown_hours: number;
  }) => {
    if (!activeOrg) return;
    try {
      await budgetAlertService.createConfig({
        ...config,
        organization_id: activeOrg.id,
      });
      await loadData();
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create config');
    }
  };

  const handleUpdateConfig = async (configId: string, updates: {
    alert_type?: 'approaching' | 'exceeded';
    threshold_percent?: number;
    email_enabled?: boolean;
    notification_enabled?: boolean;
    recipients?: string[];
    cooldown_hours?: number;
    is_active?: boolean;
  }) => {
    if (!activeOrg) return;
    try {
      await budgetAlertService.updateConfig(configId, {
        ...updates,
        organization_id: activeOrg.id,
      });
      await loadData();
      setEditingConfig(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update config');
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!activeOrg) return;
    if (!confirm(t('budget.deleteAlertConfirm'))) return;
    try {
      await budgetAlertService.deleteConfig(configId, activeOrg.id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete config');
    }
  };

  const handleTriggerCheck = async () => {
    if (!activeOrg) return;
    try {
      await budgetAlertService.triggerCheck(activeOrg.id);
      alert('Budjettitarkistus käynnistetty!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger check');
    }
  };

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  if (loading) return <PageLoading message="Ladataan budjettivaroituksia..." />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title={`🔔 Budjettivaroitukset — ${activeOrg.name}`}
        action={
          <div className="flex gap-2">
            <Button onClick={handleTriggerCheck} variant="secondary">
              🔄 Tarkista nyt
            </Button>
            <Button onClick={() => setShowCreateModal(true)} variant="primary">
              ➕ Uusi varoitus
            </Button>
          </div>
        }
      />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'configs', label: '⚙️ Varoitusasetukset' },
          { id: 'history', label: '📜 Historia' },
          { id: 'status', label: '📊 Budjettitilanne' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Configs Tab */}
      {activeTab === 'configs' && (
        <div className="space-y-4">
          {configs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">🔔</div>
              <p className="text-lg font-medium">Ei varoitusasetuksia</p>
              <p className="text-sm">Luo uusi varoitusasetus saadaksesi ilmoituksia budjetin ylityksistä.</p>
              <Button onClick={() => setShowCreateModal(true)} variant="primary" className="mt-4">
                ➕ Luo varoitusasetus
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {configs.map((config) => (
                <Card key={config.id} className="flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{config.budget_name || 'Budjetti'}</h3>
                      {config.line_item_category && (
                        <p className="text-sm text-gray-500">{config.line_item_category}</p>
                      )}
                    </div>
                    <Badge className={alertTypeColors[config.alert_type]}>
                      {alertTypeLabels[config.alert_type]}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Raja:</span>
                      <Badge className={getThresholdLevel(config.threshold_percent).color}>
                        {config.threshold_percent}%
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cooldown:</span>
                      <span>{config.cooldown_hours} h</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sähköposti:</span>
                      <span>{config.email_enabled ? '✅' : '❌'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Ilmoitus:</span>
                      <span>{config.notification_enabled ? '✅' : '❌'}</span>
                    </div>
                  </div>

                  {config.recipients && config.recipients.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Vastaanottajat:</p>
                      <div className="flex flex-wrap gap-1">
                        {config.recipients.map((email: string) => (
                          <span key={email} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {email}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-auto pt-4 border-t border-gray-200">
                    <Button
                      onClick={() => handleUpdateConfig(config.id, { is_active: !config.is_active })}
                      variant={config.is_active ? 'danger' : 'primary'}
                      size="sm"
                      className="flex-1"
                    >
                      {config.is_active ? '⚪ ' + t('common.disable') : '🟢 ' + t('common.enable')}
                    </Button>
                    <Button
                      onClick={() => setEditingConfig(config)}
                      variant="secondary"
                      size="sm"
                    >
                      ✏️
                    </Button>
                    <Button
                      onClick={() => handleDeleteConfig(config.id)}
                      variant="danger"
                      size="sm"
                    >
                      🗑️
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Table
          columns={[
            { key: 'created_at', header: 'Aika', render: (item: BudgetAlertHistory) => formatDateTime(item.created_at) },
            { key: 'budget_name', header: 'Budjetti', render: (item: BudgetAlertHistory) => item.budget_name || '-' },
            { key: 'alert_type', header: 'Tyyppi', render: (item: BudgetAlertHistory) => (
              <Badge className={alertTypeColors[item.alert_type]}>
                {alertTypeLabels[item.alert_type]}
              </Badge>
            )},
            { key: 'threshold_percent', header: 'Raja', render: (item: BudgetAlertHistory) => `${item.threshold_percent}%` },
            { key: 'actual_percent', header: 'Toteutunut', render: (item: BudgetAlertHistory) => `${item.actual_percent}%` },
            { key: 'status', header: 'Status', render: (item: BudgetAlertHistory) => (
              <Badge
                variant={
                  item.status === 'delivered'
                    ? 'success'
                    : item.status === 'failed'
                    ? 'error'
                    : 'neutral'
                }
              >
                {item.status === 'delivered' ? 'Toimitettu' : item.status === 'failed' ? 'Epäonnistui' : 'Lähetetty'}
              </Badge>
            )},
          ]}
          data={history}
          keyExtractor={(item) => item.id}
          emptyMessage="Ei varoitushistoriaa"
        />
      )}

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="space-y-4">
          {budgetStatus.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📊</div>
              <p className="text-lg font-medium">Ei budjetteja</p>
              <p className="text-sm">Luo ensin budjetti nähdäksesi tilanteen.</p>
            </div>
          ) : (
            budgetStatus.map((budget) => (
              <Card key={budget.id}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{budget.name}</h3>
                    <p className="text-sm text-gray-500">Tilikausi {budget.fiscal_year}</p>
                  </div>
                  <Badge className={getThresholdLevel(budget.spent_percent).color}>
                    {budget.spent_percent.toFixed(1)}% käytetty
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-500">Budjetti</p>
                    <p className="font-semibold">{formatEuro(budget.total_budget)}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-500">Käytetty</p>
                    <p className="font-semibold">{formatEuro(budget.total_spent)}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-500">Jäljellä</p>
                    <p className="font-semibold">
                      {formatEuro(budget.total_budget - budget.total_spent)}
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${
                      budget.spent_percent >= 100
                        ? 'bg-red-600'
                        : budget.spent_percent >= 90
                        ? 'bg-orange-500'
                        : budget.spent_percent >= 75
                        ? 'bg-blue-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(budget.spent_percent, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>75%</span>
                  <span>90%</span>
                  <span>100%</span>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create Config Modal */}
      {showCreateModal && (
        <CreateConfigModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateConfig}
          organizationId={activeOrg.id}
        />
      )}

      {/* Edit Config Modal */}
      {editingConfig && (
        <EditConfigModal
          config={editingConfig}
          onClose={() => setEditingConfig(null)}
          onSave={(updates) => handleUpdateConfig(editingConfig.id, updates)}
        />
      )}
    </div>
  );
}

// Create Config Modal Component
interface CreateConfigModalProps {
  onClose: () => void;
  onSave: (config: {
    budget_id: string;
    line_item_id?: string;
    alert_type: 'approaching' | 'exceeded';
    threshold_percent: number;
    email_enabled: boolean;
    notification_enabled: boolean;
    recipients: string[];
    cooldown_hours: number;
  }) => void;
  organizationId: string;
}

function CreateConfigModal({ onClose, onSave, organizationId }: CreateConfigModalProps) {
  const { t } = useTranslation();
  const [budgets, setBudgets] = useState<Array<{ id: string; name: string }>>([]);
  const [lineItems, setLineItems] = useState<Array<{ id: string; category: string; budget_id: string }>>([]);
  
  const [formData, setFormData] = useState({
    budget_id: '',
    line_item_id: '',
    alert_type: 'approaching' as 'approaching' | 'exceeded',
    threshold_percent: 80,
    email_enabled: true,
    notification_enabled: true,
    recipients: '',
    cooldown_hours: 24,
  });

  useEffect(() => {
    loadBudgets();
  }, []);

  useEffect(() => {
    if (formData.budget_id) {
      loadLineItems(formData.budget_id);
    }
  }, [formData.budget_id]);

  const loadBudgets = async () => {
    try {
      const status = await budgetAlertService.getBudgetStatus(organizationId);
      setBudgets(status.budgets.map((b: { id: string; name: string }) => ({ id: b.id, name: b.name })));
    } catch (err) {
      console.error('Failed to load budgets:', err);
    }
  };

  const loadLineItems = async (budgetId: string) => {
    try {
      const items = await budgetAlertService.listLineItems(organizationId, budgetId);
      setLineItems(items.map((i: { id: string; category: string; budget_id: string }) => ({ id: i.id, category: i.category, budget_id: i.budget_id })));
    } catch (err) {
      console.error('Failed to load line items:', err);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      line_item_id: formData.line_item_id || undefined,
      recipients: formData.recipients.split(',').map((email: string) => email.trim()).filter(Boolean),
    });
  };

  const budgetOptions = [{ value: '', label: t('budgetAlerts.selectBudget') }, ...budgets.map((b) => ({ value: b.id, label: b.name }))];
  const lineItemOptions = [{ value: '', label: 'Koko budjetti' }, ...lineItems.map((i) => ({ value: i.id, label: i.category }))];
  const alertTypeOptions = [
    { value: 'approaching', label: 'Lähestymässä rajaa' },
    { value: 'exceeded', label: 'Ylitetty' },
  ];

  return (
    <Modal onClose={onClose} title="➕ Uusi budjettivaroitus">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Budjetti *</label>
          <Select
            value={formData.budget_id}
            onChange={(e) => setFormData({ ...formData, budget_id: e.target.value, line_item_id: '' })}
            options={budgetOptions}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategoriasyvyys (valinnainen)</label>
          <Select
            value={formData.line_item_id}
            onChange={(e) => setFormData({ ...formData, line_item_id: e.target.value })}
            options={lineItemOptions}
            disabled={!formData.budget_id}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Varoitustyyppi *</label>
          <Select
            value={formData.alert_type}
            onChange={(e) => setFormData({ ...formData, alert_type: e.target.value as 'approaching' | 'exceeded' })}
            options={alertTypeOptions}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Raja-% ({formData.alert_type === 'approaching' ? '75-99%' : '100%'})
          </label>
          <Input
            type="number"
            min={1}
            max={100}
            value={formData.threshold_percent}
            onChange={(e) => setFormData({ ...formData, threshold_percent: parseInt(e.target.value) })}
            required
          />
        </div>

        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.email_enabled}
              onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sähköposti</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.notification_enabled}
              onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sovellusilmoitus</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vastaanottajat (pilkulla eroteltu)</label>
          <Input
            type="text"
            value={formData.recipients}
            onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
            placeholder="email1@example.com, email2@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cooldown (tuntia)</label>
          <Input
            type="number"
            min={1}
            value={formData.cooldown_hours}
            onChange={(e) => setFormData({ ...formData, cooldown_hours: parseInt(e.target.value) })}
            required
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
          <Button type="submit" variant="primary">
            💾 Luo varoitus
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Config Modal Component
interface EditConfigModalProps {
  config: BudgetAlertConfig;
  onClose: () => void;
  onSave: (updates: {
    alert_type?: 'approaching' | 'exceeded';
    threshold_percent?: number;
    email_enabled?: boolean;
    notification_enabled?: boolean;
    recipients?: string[];
    cooldown_hours?: number;
    is_active?: boolean;
  }) => void;
}

function EditConfigModal({ config, onClose, onSave }: EditConfigModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    alert_type: config.alert_type,
    threshold_percent: config.threshold_percent,
    email_enabled: config.email_enabled,
    notification_enabled: config.notification_enabled,
    recipients: config.recipients?.join(', ') || '',
    cooldown_hours: config.cooldown_hours,
    is_active: config.is_active,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      alert_type: formData.alert_type,
      threshold_percent: formData.threshold_percent,
      email_enabled: formData.email_enabled,
      notification_enabled: formData.notification_enabled,
      recipients: formData.recipients.split(',').map((email: string) => email.trim()).filter(Boolean),
      cooldown_hours: formData.cooldown_hours,
      is_active: formData.is_active,
    });
  };

  const alertTypeOptions = [
    { value: 'approaching', label: 'Lähestymässä rajaa' },
    { value: 'exceeded', label: 'Ylitetty' },
  ];

  return (
    <Modal onClose={onClose} title={'✏️ ' + t('budgetAlerts.editAlertSetting')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Varoitustyyppi</label>
          <Select
            value={formData.alert_type}
            onChange={(e) => setFormData({ ...formData, alert_type: e.target.value as 'approaching' | 'exceeded' })}
            options={alertTypeOptions}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Raja-%</label>
          <Input
            type="number"
            min={1}
            max={100}
            value={formData.threshold_percent}
            onChange={(e) => setFormData({ ...formData, threshold_percent: parseInt(e.target.value) })}
            required
          />
        </div>

        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.email_enabled}
              onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sähköposti</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.notification_enabled}
              onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sovellusilmoitus</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vastaanottajat</label>
          <Input
            type="text"
            value={formData.recipients}
            onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
            placeholder="email1@example.com, email2@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cooldown (tuntia)</label>
          <Input
            type="number"
            min={1}
            value={formData.cooldown_hours}
            onChange={(e) => setFormData({ ...formData, cooldown_hours: parseInt(e.target.value) })}
            required
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
          <Button type="submit" variant="primary">
            💾 Tallenna
          </Button>
        </div>
      </form>
    </Modal>
  );
}
